import { EventBus } from "./event-bus.js";
import { PhysicsStore } from "./store.js";
import type { ActionPayload, PhysicsActionId } from "../interaction/action-controller.js";
import type { ExperimentPack, LabMode } from "../data/schemas.js";
import { MissionEngine, type StepTransition } from "../mission/mission-engine.js";
import { TelemetryClient, type Reporter } from "../telemetry/telemetry-client.js";

export interface PhysicsEngineOptions {
  instanceId: string;
  sessionId: string;
  reporter?: Reporter;
}

/**
 * 引擎顶层挂载点：装配 EventBus/Store/MissionEngine/TelemetryClient。对应 tech/108 §4.1。
 *
 * 遥测层只订阅 eventBus（`telemetry.start`），不反向调用 mission-engine——关掉 reporter，
 * 实验照常可跑完（tech/108 §2 的验收红线）。
 */
export class PhysicsEngine {
  readonly eventBus = new EventBus();
  readonly store = new PhysicsStore();
  private mission: MissionEngine | undefined;
  private telemetry: TelemetryClient | undefined;
  private unsubscribeTelemetry: (() => void) | undefined;
  private loadedPack: ExperimentPack | undefined;

  constructor(private readonly options: PhysicsEngineOptions) {}

  load(pack: ExperimentPack): void {
    this.loadedPack = pack;
    this.store.reset();
    for (const variable of pack.mission.variables) {
      this.store.variables.define(variable);
    }
    this.mission = new MissionEngine(pack, this.store, this.eventBus);

    this.unsubscribeTelemetry?.();
    this.telemetry = new TelemetryClient({
      instanceId: this.options.instanceId,
      sessionId: this.options.sessionId,
      experimentId: pack.experiment.id,
      experimentVersionId: pack.experiment.schemaVersion,
      reporter: this.options.reporter,
    });
    this.unsubscribeTelemetry = this.telemetry.start(this.eventBus);
  }

  start(_mode: LabMode = "learn"): void {
    this.requireMission().start();
  }

  submitChoice(optionId: string): StepTransition {
    return this.requireMission().submitChoice(optionId);
  }

  performAction(actionId: PhysicsActionId, payload?: ActionPayload): StepTransition {
    return this.requireMission().performAction(actionId, payload);
  }

  recordTrial(): StepTransition {
    return this.requireMission().recordTrial();
  }

  returnToAct(): StepTransition {
    return this.requireMission().returnToAct();
  }

  submitExam(optionId: string): StepTransition {
    return this.requireMission().submitExam(optionId);
  }

  submitSummary(): StepTransition {
    return this.requireMission().submitSummary();
  }

  submitKnowledge(): StepTransition {
    return this.requireMission().submitKnowledge();
  }

  requestHint(): void {
    this.requireMission().requestHint();
  }

  get currentStep() {
    return this.requireMission().currentStep;
  }

  get currentStage() {
    return this.requireMission().currentStage;
  }

  /** 已加载的实验包，供渲染层查知识库/Stage 列表——不暴露引擎内部状态。 */
  get pack(): ExperimentPack | undefined {
    return this.loadedPack;
  }

  reset(): void {
    if (!this.loadedPack) return;
    this.load(this.loadedPack);
  }

  dispose(): void {
    this.unsubscribeTelemetry?.();
    this.eventBus.clear();
  }

  private requireMission(): MissionEngine {
    if (!this.mission) {
      throw new Error("[PhysicsEngine] 尚未 load 实验包");
    }
    return this.mission;
  }
}
